import React from 'react';
import { List, Typography, Popper, Paper, ClickAwayListener } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { StyledListItem } from './MuiSidebarMenu.styles';
import { MenuItem } from '../../types';

const slideRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedPaper = styled(Paper)({
  animation: `${slideRight} 0.2s ease-out`,
  backgroundColor: '#2a3042',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
});

const AnimatedListItem = styled(StyledListItem)({
  animation: `${fadeIn} 0.2s ease-out`,
  animationFillMode: 'backwards',
});

interface CollapsedTooltipProps {
    showPopper: boolean;
    popperAnchor?: HTMLElement | null;
    item: MenuItem;
    currentPath: string;
    hoveredSubItem?: MenuItem | null;
    showSubMenu: boolean;
    subMenuAnchor?: HTMLElement | null;
    onPopperMouseEnter: () => void;
    onPopperMouseLeave: () => void;
    onSubItemHover: (subItem: MenuItem, event: React.MouseEvent) => void;
    onSubItemLeave: () => void;
    onSubMenuMouseEnter: () => void;
    onSubMenuMouseLeave: () => void;
    onPopperClickAway: () => void;
    onItemClick?: (item: MenuItem) => void;
    onCloseMenus: () => void;
}

interface SubMenuProps {
    items: MenuItem[];
    currentPath: string;
    title: string;
    hoveredItem?: MenuItem;
    fontSize?: string;
    minHeight?: number;
    onItemClick?: (item: MenuItem) => void;
    onCloseMenus: () => void;
    onItemHover?: (item: MenuItem, event: React.MouseEvent) => void;
    onItemLeave?: () => void;
}

const SubMenu = ({ 
    items, 
    currentPath, 
    title, 
    hoveredItem,
    fontSize = '12px',
    minHeight = 36,
    onItemClick, 
    onCloseMenus,
    onItemHover,
    onItemLeave
}: SubMenuProps) => (
    <>
        <Typography sx={{ px: 2, py: 1, color: '#fff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', fontSize, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
            {title}
        </Typography>
        <List component="div" disablePadding>
            {items.map((item, index) => {
                if (item.component) {
                    const Component = item.component;
                    return (
                        <AnimatedListItem key={item.id || index} level={0} sx={{ animationDelay: `${index * 0.05}s` }}>
                            <Component {...(item.componentProps || {})} />
                        </AnimatedListItem>
                    );
                }

                const isActive = currentPath === item.path;
                const isHovered = hoveredItem === item;
                
                return (
                    <AnimatedListItem
                        key={item.id || item.label || index}
                        level={0}
                        sx={{
                            px: 2,
                            minHeight,
                            backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                            animationDelay: `${index * 0.05}s`,
                        }}
                        onClick={() => {
                            if (item.path && onItemClick) {
                                onItemClick(item);
                                onCloseMenus();
                            }
                        }}
                        onMouseEnter={(e) => onItemHover?.(item, e)}
                        onMouseLeave={onItemLeave}
                    >
                        <Typography variant="body2" sx={{ color: isActive ? '#fff' : '#a6b0cf', fontSize, pl: 1, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
                            {item.label}
                        </Typography>
                    </AnimatedListItem>
                );
            })}
        </List>
    </>
);

const CollapsedTooltip = ({
    showPopper,
    popperAnchor,
    item,
    currentPath,
    hoveredSubItem,
    showSubMenu,
    subMenuAnchor,
    onPopperMouseEnter,
    onPopperMouseLeave,
    onSubItemHover,
    onSubItemLeave,
    onSubMenuMouseEnter,
    onSubMenuMouseLeave,
    onPopperClickAway,
    onItemClick,
    onCloseMenus
}: CollapsedTooltipProps) => {
    if (!showPopper) return null;

    return (
        <>
            <Popper open={showPopper} anchorEl={popperAnchor} placement="right-start" style={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={onPopperClickAway}>
                    <AnimatedPaper elevation={8} onMouseEnter={onPopperMouseEnter} onMouseLeave={onPopperMouseLeave} sx={{ minWidth: 200, py: 1 }}>
                        <SubMenu
                            items={item.subMenu || []}
                            currentPath={currentPath}
                            title={item.label}
                            hoveredItem={hoveredSubItem || undefined}
                            onItemClick={onItemClick}
                            onCloseMenus={onCloseMenus}
                            onItemHover={onSubItemHover}
                            onItemLeave={onSubItemLeave}
                        />
                    </AnimatedPaper>
                </ClickAwayListener>
            </Popper>

            {showSubMenu && hoveredSubItem?.subMenu && (
                <Popper open={showSubMenu} anchorEl={subMenuAnchor} placement="right-start" style={{ zIndex: 1301 }}>
                    <AnimatedPaper elevation={8} onMouseEnter={onSubMenuMouseEnter} onMouseLeave={onSubMenuMouseLeave} sx={{ minWidth: 180, py: 1 }}>
                        <SubMenu
                            items={hoveredSubItem.subMenu}
                            currentPath={currentPath}
                            title={hoveredSubItem.label}
                            fontSize="11px"
                            minHeight={32}
                            onItemClick={onItemClick}
                            onCloseMenus={onCloseMenus}
                        />
                    </AnimatedPaper>
                </Popper>
            )}
        </>
    );
};

export default CollapsedTooltip;
